<template>
  <div style="display: flex">
    <div :class="sizeMap" style="flex: 1; margin: 10px">
      <l-map
        ref="leafletMap"
        style="height: 100%; width: 100%"
        :zoom="zoom"
        :center="mapCenter"
        :options="{ attributionControl: false }"
        @moveend="updateSiteCenter"
      >
        <l-tile-layer
          v-for="tileProvider in tileProviders"
          :key="tileProvider.name"
          :name="tileProvider.name"
          :visible="tileProvider.visible"
          :url="tileProvider.url"
          layer-type="base"
        />
        <l-control-layers position="topright" style="margin: 20px">
        </l-control-layers>

        <l-control
          :visible="true"
          position="bottomleft"
          style="z-index: 10000; position: absolute; bottom: 0px"
        >
          <v-btn style="z-index: 10000" @click="resizeMap()">
            <v-icon> {{ buttonSymbole }} </v-icon>
          </v-btn>
        </l-control>

        <l-marker
          v-for="site in visibleSites"
          :key="site.installation_id"
          :lat-lng="[site.latitude, site.longitude]"
          :icon="getIconForSite(site)"
        >
          <l-popup>
            <b>
              {{ site.installation_name }}
            </b>
            <br />
            {{ site.address }}
            <br />
            {{ site.postalCode }}
            <br />
            {{ site.country }}
            <br />
            {{ roundtwodec(site.pdc_max) }} kwc
            <br />
            <hr />
          </l-popup>
        </l-marker>
      </l-map>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { mapGetters } from "vuex";

export default {
  props: {
    selectsite: { type: Array, default: () => [] },
    liste_parc: { type: Array, default: () => [] },
  },
  mounted() {
    this.$nextTick(() => {
      this.initMapPosition();
      this.updateMapMarkers();
    });
    this.zoom = 4;
  },
  data() {
    return {
      buttonSymbole: "mdi-arrow-bottom-left",
      zoom: 4,
      sitecenter: "",
      sizeMap: "small-map",
      mapCenter: [46.6034, 3.1237],
      defaultZoom: 15,
      tileProviders: [
        {
          url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}.png",
          visible: true,
          name: "satelliteESRI",
          attribution:
            "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
        },

        {
          url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
          visible: true,
          name: "OpenTopoMap",
          attribution:
            'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
        },
        {
          url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          visible: true,
          name: "OpenStreetMap",
          attribution:
            '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        },
      ],
    };
  },

  computed: {
    ...mapGetters(["role", "cur_install_id", "constants", "installations"]),
    installation() {
      const routeId = this.$route.params.id;
      if (!routeId || !this.installations.length) return null;

      return this.installations.find(
        // Change x.id to x.installation_id if that is what the URL represents
        (x) => String(x.installation_id) === String(routeId)
      );
    },
    visibleSites() {
      if (this.selectsite && this.selectsite.length > 0) {
        // 1. Convert all selected IDs to Strings first for a fast, reliable match
        const selectedStrings = this.selectsite.map((id) => String(id));

        return this.installations.filter((element) =>
          // 2. Convert the element ID to String as well
          selectedStrings.includes(String(element.installation_id))
        );
      }

      // Case B: Direct access via URL
      return this.installation ? [this.installation] : [];
    },
  },

  watch: {
    zoom: {
      handler() {
        this.sitecenter = this.searchsite();
      },
    },
    installations: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.$nextTick(() => {
            this.initMapPosition();
          });
        }
      },
      immediate: true,
    },
    selectsite: {
      immediate: true,
      handler() {
        this.initMapPosition();
      },
    },
  },

  methods: {
    initMapPosition() {
      if (!this.$refs.leafletMap || !this.$refs.leafletMap.mapObject) {
        return;
      }

      const sites = this.takeselectsites();

      if (!sites.length) return;

      const map = this.$refs.leafletMap.mapObject;

      this.centerMap(sites[0].latitude, sites[0].longitude);
      map.invalidateSize();
    },
    updateMapMarkers() {
      // 1. Add the check here
      if (this.$refs.leafletMap && this.$refs.leafletMap.mapObject) {
        const map = this.$refs.leafletMap.mapObject;
        map.invalidateSize();
      }
    },

    centerMap(lat, long) {
      const map = this.$refs.leafletMap.mapObject;
      if (map) {
        // Move to the site at zoom 15
        map.setView([lat, long], 15);
        // Sync the internal data so Vue doesn't fight the map
        this.zoom = 4;
      }
    },

    resizeMap() {
      const map = this.$refs.leafletMap.mapObject;
      if (this.sizeMap === "full-screen-map") {
        this.sizeMap = "small-map";
        this.buttonSymbole = "mdi-arrow-bottom-left";
      } else {
        this.sizeMap = "full-screen-map";
        this.buttonSymbole = "mdi-arrow-top-right";
      }
      this.$nextTick(() => {
        map.invalidateSize();
      });
    },

    updateSiteCenter() {
      this.sitecenter = this.searchsite();
    },

    searchsite() {
      const map = this.$refs.leafletMap.mapObject;
      const center = map.getCenter();
      let centersite = "";

      this.takeselectsites().forEach((element) => {
        const isLatitudeClose =
          Math.abs(element.latitude - center.lat) < 0.0001;
        const isLongitudeClose =
          Math.abs(element.longitude - center.lng) < 0.0001;
        if (isLatitudeClose && isLongitudeClose) {
          centersite = element.installation_name;
          return;
        }
      });
      return centersite;
    },

    roundtwodec(nombre) {
      return Math.round(nombre / 1000);
    },

    takeselectsites() {
      // Case A: Sites are selected from the fleet list
      if (this.selectsite && this.selectsite.length > 0) {
        return this.installations.filter((element) =>
          this.selectsite.includes(element.installation_id)
        );
      }

      // Case B: NEW/RE-ADDED - Direct access via URL (one specific site)
      // This ensures that if no list is selected, we still show the marker for the current ID
      if (this.installation) {
        return [this.installation];
      }

      return [];
    },

    calculPR(item) {
      if (!this.liste_parc || this.liste_parc.length === 0) {
        return 10001;
      }
      let evo = 10001;
      this.liste_parc.forEach((elm) => {
        if (elm.id == item.installation_id) {
          evo = elm.evolution_ip_clean;
        }
      });
      return evo;
    },

    getIconForSite(sites) {
      let iconUrl;
      let site = Array.isArray(sites) ? sites[0] : sites;
      if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Batiment agricole / Toiture") !== -1
      ) {
        if (this.calculPR(site) >= 0 && this.calculPR(site) <= 10000) {
          iconUrl = require("../parc/ressources/BatAgriToitV.png");
        } else if (this.calculPR(site) < 0 && this.calculPR(site) >= -5) {
          iconUrl = require("../parc/ressources/BatAgriToitO.png");
        } else if (this.calculPR(site) < -5) {
          iconUrl = require("../parc/ressources/BatAgriToitR.png");
        } else {
          iconUrl = require("../parc/ressources/BatAgriToitG.png");
        }
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Centrale au sol") !== -1
      ) {
        if (this.calculPR(site) >= 0 && this.calculPR(site) <= 10000) {
          iconUrl = require("../parc/ressources/SolV.png");
        } else if (this.calculPR(site) < 0 && this.calculPR(site) >= -5) {
          iconUrl = require("../parc/ressources/SolO.png");
        } else if (this.calculPR(site) < -5) {
          iconUrl = require("../parc/ressources/SolR.png");
        } else {
          iconUrl = require("../parc/ressources/SolG.png");
        }
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Batiment tertiaire / Toiture") !== -1
      ) {
        if (this.calculPR(site) >= 0 && this.calculPR(site) <= 10000) {
          iconUrl = require("../parc/ressources/BatTertToitV.png");
        } else if (this.calculPR(site) < 0 && this.calculPR(site) >= -5) {
          iconUrl = require("../parc/ressources/BatTertToitO.png");
        } else if (this.calculPR(site) < -5) {
          iconUrl = require("../parc/ressources/BatTertToitR.png");
        } else {
          iconUrl = require("../parc/ressources/BatTertToitG.png");
        }
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Tertiaire") !== -1
      ) {
        if (this.calculPR(site) >= 0 && this.calculPR(site) <= 10000) {
          iconUrl = require("../parc/ressources/BatTertToitV.png");
        } else if (this.calculPR(site) < 0 && this.calculPR(site) >= -5) {
          iconUrl = require("../parc/ressources/BatTertToitO.png");
        } else if (this.calculPR(site) < -5) {
          iconUrl = require("../parc/ressources/BatTertToitR.png");
        } else {
          iconUrl = require("../parc/ressources/BatTertToitG.png");
        }
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Batiment industriel / Toiture") !== -1
      ) {
        if (this.calculPR(site) >= 0 && this.calculPR(site) <= 10000) {
          iconUrl = require("../parc/ressources/BatInduV.png");
        } else if (this.calculPR(site) < 0 && this.calculPR(site) >= -5) {
          iconUrl = require("../parc/ressources/BatInduO.png");
        } else if (this.calculPR(site) < -5) {
          iconUrl = require("../parc/ressources/BatInduR.png");
        } else {
          iconUrl = require("../parc/ressources/BatInduG.png");
        }
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Batiment agricole / Serre") !== -1
      ) {
        if (this.calculPR(site) >= 0 && this.calculPR(site) <= 10000) {
          iconUrl = require("../parc/ressources/BatAgriSerreV.png");
        } else if (this.calculPR(site) < 0 && this.calculPR(site) >= -5) {
          iconUrl = require("../parc/ressources/BatAgriSerreO.png");
        } else if (this.calculPR(site) < -5) {
          iconUrl = require("../parc/ressources/BatAgriSerreR.png");
        } else {
          iconUrl = require("../parc/ressources/BatAgriSerreG.png");
        }
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Batiment résidentiel") !== -1
      ) {
        if (this.calculPR(site) >= 0 && this.calculPR(site) <= 10000) {
          iconUrl = require("../parc/ressources/BatResidentielV.png");
        } else if (this.calculPR(site) < 0 && this.calculPR(site) >= -5) {
          iconUrl = require("../parc/ressources/BatResidentielO.png");
        } else if (this.calculPR(site) < -5) {
          iconUrl = require("../parc/ressources/BatResidentielR.png");
        } else {
          iconUrl = require("../parc/ressources/BatResidentielG.png");
        }
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Ombriere parking") !== -1
      ) {
        if (this.calculPR(site) >= 0 && this.calculPR(site) <= 10000) {
          iconUrl = require("../parc/ressources/OmbParkV.png");
        } else if (this.calculPR(site) < 0 && this.calculPR(site) >= -5) {
          iconUrl = require("../parc/ressources/OmbParkO.png");
        } else if (this.calculPR(site) < -5) {
          iconUrl = require("../parc/ressources/OmbParkR.png");
        } else {
          iconUrl = require("../parc/ressources/OmbParkG.png");
        }
      } else {
        if (this.calculPR(site) >= 0 && this.calculPR(site) <= 10000) {
          iconUrl = require("../parc/ressources/DefaultV.png");
        } else if (this.calculPR(site) < 0 && this.calculPR(site) >= -5) {
          iconUrl = require("../parc/ressources/DefaultO.png");
        } else if (this.calculPR(site) < -5) {
          iconUrl = require("../parc/ressources/DefaultR.png");
        } else {
          iconUrl = require("../parc/ressources/DefaultG.png");
        }
      }

      const IndustrielToitIcon = L.icon({
        iconUrl: iconUrl,
        iconSize: [20, 20],
        iconAnchor: [10, 20],
        popupAnchor: [-17, -50],
      });
      const SolIcon = L.icon({
        iconUrl: iconUrl,
        iconSize: [20, 20],
        iconAnchor: [10, 20],
        popupAnchor: [-17, -50],
      });
      const defaultIcon = L.icon({
        iconUrl: iconUrl,
        iconSize: [20, 20],
        iconAnchor: [10, 20],
        popupAnchor: [-17, -50],
      });
      const TertiaireToitIcon = L.icon({
        iconUrl: iconUrl,
        iconSize: [20, 20],
        iconAnchor: [10, 20],
        popupAnchor: [-17, -50],
      });
      const AgricoleToitIcon = L.icon({
        iconUrl: iconUrl,
        iconSize: [20, 20],
        iconAnchor: [10, 20],
        popupAnchor: [-17, -50],
      });
      const AgricoleSerreIcon = L.icon({
        iconUrl: iconUrl,
        iconSize: [20, 20],
        iconAnchor: [10, 20],
        popupAnchor: [-17, -50],
      });
      const ResidentielIcon = L.icon({
        iconUrl: iconUrl,
        iconSize: [20, 20],
        iconAnchor: [10, 20],
        popupAnchor: [-17, -50],
      });
      const OmbriereParkIcon = L.icon({
        iconUrl: iconUrl,
        iconSize: [20, 20],
        iconAnchor: [10, 20],
        popupAnchor: [-17, -50],
      });

      if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Batiment agricole / Toiture") !== -1
      ) {
        return AgricoleToitIcon;
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Centrale au sol") !== -1
      ) {
        return SolIcon;
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Batiment tertiaire / Toiture") !== -1
      ) {
        return TertiaireToitIcon;
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Tertiaire") !== -1
      ) {
        return TertiaireToitIcon;
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Batiment industriel / Toiture") !== -1
      ) {
        return IndustrielToitIcon;
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Batiment agricole / Serre") !== -1
      ) {
        return AgricoleSerreIcon;
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Batiment résidentiel") !== -1
      ) {
        return ResidentielIcon;
      } else if (
        typeof site.installation_type === "string" &&
        site.installation_type.indexOf("Ombriere parking") !== -1
      ) {
        return OmbriereParkIcon;
      } else {
        return defaultIcon;
      }
    },
  },
};
</script>

<style>
.full-screen-map {
  height: 100vh;
  width: 100vw;
  position: fixed !important;
  top: 0px;
  left: 0px;
  z-index: 10;
}
.small-map {
  width: 100%;
  height: 217px;
  margin-top: 20px;
  z-index: 1;
}
.leaflet-marker-icon {
  max-width: none !important;
  max-height: none !important;
}
</style>
